import { FC, ReactNode } from 'react';
import { Col, Row, Typography } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { BaseSkeleton } from '@/shared/components/skeletons';
import SmallTag from '@/shared/components/tags/SmallTag';
import TickerExchangeTag from '@/shared/components/tags/TickerExchangeTag';

import { FinancialOverviewDetailsProps } from '../../types';
import { FinancialOverviewDescriptions } from '../FinancialOverviewDescriptions';

export const FinancialOverviewDetails: FC<FinancialOverviewDetailsProps> = ({
    exchange,
    fields,
    labelColor = 'secondary',
    loading,
    symbol,
    title,
    website,
}) => {
    const { token } = useAntDesignTheme();
    const labelToken = labelColor === 'tertiary' ? token.colorTextTertiary : token.colorTextSecondary;

    return (
        <>
            <Row align='middle' gutter={[16, 8]} style={{ marginTop: '0.25rem' }} wrap={false}>
                <Col flex='auto' style={{ minWidth: 0 }}>
                    {loading ? (
                        <BaseSkeleton
                            style={{ marginBottom: 0, paddingBottom: 0, verticalAlign: '-1rem' }}
                            paragraph={{ rows: 0 }}
                        />
                    ) : (
                        <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            <Typography.Title style={{ margin: 0 }}>{title}</Typography.Title>
                            <TagLikeExchange
                                companyName={typeof title === 'string' ? title : undefined}
                                exchange={exchange}
                                labelColor={labelToken}
                                symbol={symbol}
                                website={website}
                            />
                        </div>
                    )}
                </Col>
            </Row>
            <FinancialOverviewDescriptions
                items={fields.map((field) => ({
                    icon: field.icon,
                    key: field.label,
                    label: field.label,
                    value: field.value,
                }))}
                labelColor={labelToken}
                style={{ marginTop: '1.25rem' }}
            />
        </>
    );
};

const TagLikeExchange: FC<{
    companyName?: string;
    exchange?: ReactNode;
    labelColor: string;
    symbol?: ReactNode;
    website?: string;
}> = ({ companyName, exchange, labelColor, symbol, website }) => {
    const { token } = useAntDesignTheme();

    if (typeof symbol === 'string' && exchange !== 'Imported') {
        return (
            <TickerExchangeTag
                companyName={companyName}
                compact
                exchange={typeof exchange === 'string' ? exchange : undefined}
                exchangeStyle={{ color: labelColor }}
                symbol={symbol}
                symbolStyle={{ color: token.colorText }}
                website={website}
            />
        );
    }

    return (
        <SmallTag
            compact
            message={
                <>
                    <span style={{ color: labelColor }}>{exchange || 'N/A'}:</span>&nbsp;
                    <span style={{ color: token.colorText }}>{symbol || 'N/A'}</span>
                </>
            }
            styles={{ marginLeft: 0 }}
        />
    );
};
